import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { actionCreators } from "./redux/checkInSaga";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import SectionToggle from "../../../components/sectionToggle/sectionToggle";
import YearOverYear from "./smartComponents/YearOverYear";
import DatePicker from "./smartComponents/DatePicker";
import CustomDateRanges from "./smartComponents/CustomDateRanges";
import PeriodType from "./smartComponents/PeriodType";
import { DataPoints } from "./smartComponents/DataPoints";
import ToggleableButton from "../../../components/toggleableButton/toggleableButton";
import { addSticky } from "../../../utils/addSticky";
import CheckInLearnMore from "./smartComponents/CheckInLearnMore";
import HeatMapChart from "./smartComponents/HeatMapChart";

export const CheckInModule = ({
  fetchCurrentUser,
  currentUser,
  numberOfMonths,
  isLoading,
  startDate,
  endDate,
  series,
  updatesLoading,
  currentSelectedCategory,
  yearOverYearLoading,
  sourceType,
  groupByWeekDay,
  showPercentage,
  onChangePercentageToggle,
  onChangeToggle,
  filtersDirty,
  updateChart
}) => {
  const [filterDirty, setFiltersDirty] = useState(filtersDirty);

  useEffect(() => {
    fetchCurrentUser({
      currentUser: currentUser,
      numberOfMonths: numberOfMonths
    });
  }, []);

  useEffect(() => {
    if (!isLoading) {
      addSticky();
    }
  }, [isLoading]);

  const resetData = () => {
    fetchCurrentUser({
      currentUser: currentUser,
      numberOfMonths: numberOfMonths
    });

    setFiltersDirty(false);
  };

  const renderUpdateBtn = () => {
    if (filtersDirty && !isLoading && startDate !== "Invalid date" && endDate !== "Invalid date") {
      return (
        <button className="submit" onClick={() => updateChart()}>
          Update
        </button>
      );
    }

    return null;
  };

  const renderResetBtn = () => {
    if (filtersDirty & !isLoading) {
      return (
        <button className="submit reset" onClick={() => resetData()}>
          Reset
        </button>
      );
    }

    return null;
  };

  const updateButton = renderUpdateBtn();
  const resetButton = renderResetBtn();

  return (
    <div className="check-in-module">
      <SectionToggle
        maxHeight={"auto"}
        autoHeight
        title="Check-Ins"
        customClass="class-check-in-heatmap withSticky"
        hideToggle
        isLoading={isLoading}
        // learnMore={<CheckInLearnMore sourceType={sourceType} />}
        content={
          <div>
            <div className="edit-selections-bar" id="sticky-select-bar">
              <div className="selection-bar-filters">
                <div className="select-box" style={{ width: "360px" }}>
                  <p className="offers-title">Date Range</p>
                  {!filterDirty && <CustomDateRanges />}
                </div>
                <div className="date-select-box select-box multi">
                  <p className="offers-title">Date</p>
                  {!filterDirty && <DatePicker />}
                </div>
              </div>

              {updateButton || resetButton ? (
                <div className="action-buttons">
                  {updateButton}
                  {resetButton}
                </div>
              ) : null}
            </div>
            {series.length !== 0 ? (
              <>
                {updatesLoading ? (
                  <LoadingAnimation text="Updating heatmap..." />
                ) : (
                  <div className="content-wrapper">
                    <DataPoints />
                    <div className="chart-header">
                      <h2>Total {currentSelectedCategory} Breakdown</h2>
                      <div className="select-box" style={{ width: "200px" }}>
                        {yearOverYearLoading ? (
                          <button className="picky__input">
                            <LoadingAnimation uiLoader />{" "}
                          </button>
                        ) : (
                          <PeriodType />
                        )}
                      </div>
                    </div>
                    <YearOverYear
                      margin={{ top: 5, right: 60, left: 5, bottom: 5 }}
                      cartesianGridVerticalPoints={(width) => [width - 60]} // To place the vertical line at the right we need the width minus margin-right
                    />
                  </div>
                )}

                <div style={{ paddingTop: "50px" }}>
                  <h2>{currentSelectedCategory} Heatmap</h2>
                  <div className="toggle-list">
                    {sourceType !== "marianatek" ? (
                      <div className="check-in-toggle">
                        <p>Group by Day of the Week</p>
                        <ToggleableButton
                          title="Group by Day of the Week"
                          isRadio
                          isActive={groupByWeekDay}
                          onClick={(isActive) => onChangeToggle(isActive)}
                        />
                      </div>
                    ) : null}
                    {sourceType === "clubready" && !groupByWeekDay ? (
                      <div className="check-in-toggle">
                        <p>Show percentage</p>
                        <ToggleableButton
                          title="Show percentage"
                          isRadio
                          isActive={showPercentage}
                          onClick={(isActive) => onChangePercentageToggle(isActive)}
                        />
                      </div>
                    ) : null}
                  </div>

                  {updatesLoading ? (
                    <LoadingAnimation text="Updating heatmap..." />
                  ) : (
                    <HeatMapChart />
                  )}
                </div>
              </>
            ) : (
              <p className="error">No data found, please update filters and try again.</p>
            )}
          </div>
        }
      />
    </div>
  );
};

export default connect(
  (state) => ({
    isLoading: state.checkIn.isLoading,
    userParams: state.checkIn.currentUser,
    updatesLoading: state.checkIn.updatesLoading,
    series: state.checkIn.series,
    filtersDirty: state.checkIn.filtersDirty,
    startDate: state.checkIn.startDate,
    endDate: state.checkIn.endDate,
    customDateRangeLoading: state.checkIn.customDateRangeLoading,
    yearOverYearLoading: state.checkIn.yearOverYearLoading,
    currentSelectedCategory: state.checkIn.currentSelectedCategory,
    groupByWeekDay: state.checkIn.groupByWeekDay,
    showPercentage: state.checkIn.showPercentage,
    sourceType: state.checkIn.currentUser.sourceType
  }),
  {
    fetchCurrentUser: actionCreators.fetchCurrentUser,
    updateChart: actionCreators.updateData,
    onChangeToggle: actionCreators.onChangeToggle,
    onChangePercentageToggle: actionCreators.onChangePercentageToggle
  }
)(CheckInModule);

CheckInModule.propTypes = {
  currentUser: PropTypes.object,
  fetchCurrentUser: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  series: PropTypes.array,
  filtersDirty: PropTypes.bool,
  updateChart: PropTypes.func,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  classFiltersLoading: PropTypes.bool,
  numberOfMonths: PropTypes.number,
  yearOverYearLoading: PropTypes.bool,
  updatesLoading: PropTypes.bool,
  currentSelectedCategory: PropTypes.string,
  groupByWeekDay: PropTypes.bool,
  showPercentage: PropTypes.bool,
  onChangeToggle: PropTypes.func,
  onChangePercentageToggle: PropTypes.func,
  sourceType: PropTypes.string
};
